import { useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { erpApi } from "@/shared/axios/erp-api";
import { urlBackend } from "@/shared/constants";
import { useAuthStore } from "@/store/auth/auth.store";
import { EtapaFotoTraslado, TipoFotoProceso } from "./fotos-traslado.utils";

/**
 * Fotos del TRASLADO contra el endpoint dedicado del backend
 * (`/orden-trabajo/<id>/fotos-traslado/`): consulta ligera para sondeo en
 * tiempo real, eliminar (solo Activaciones) y volver a subir las eliminadas
 * (técnico). Los arreglos de ingreso/proceso incluyen las MARCAS de fotos
 * eliminadas (items sin `photoUrl`); ver fotos-traslado.utils.
 * No usa el PUT genérico de la OT, así que no dispara sus efectos ni pisa los
 * cambios que la otra parte haya hecho mientras tanto.
 */
/**
 * Flujo al que pertenece la OT. El backend expone el MISMO mecanismo para
 * traslados (`fotos-traslado/`) y visitas (`fotos-visita/`): marcas de fotos
 * eliminadas por Activaciones, sondeo en vivo y reposición por el técnico.
 */
export type FlujoFotos = "traslado" | "visita";

export interface FotosTrasladoSnapshot {
  id_orden_trabajo: string;
  /** "TRASLADO" | "VISITA" (lo pone el backend). */
  flujo?: string;
  urls_fotos_ingreso: any[];
  urls_fotos_proceso: any[];
  urls_fotos_salida: any;
  fecha_hora_inicio_instalacion: string | null;
  fecha_hora_fin_instalacion: string | null;
  fecha_hora_inicio_visita?: string | null;
  fecha_hora_fin_visita?: string | null;
  estado_orden_trabajo: string | null;
  estado_activacion: string | null;
  conteo: { ingreso: number; proceso: number; finalizacion: number };
  /** Fotos que Activaciones eliminó y el técnico aún no vuelve a subir. */
  pendientes: { ingreso: number; proceso: number; finalizacion: number };
  requeridas: { ingreso: number; proceso: number; finalizacion: number };
  /**
   * Excedente de fibra del traslado (agendamiento.observaciones.excedente_fibra),
   * o null. Ver otros-conceptos-traslado/excedente-fibra.utils.
   */
  excedente_fibra?: any | null;
  /** Estado de aprobación de los "otros conceptos" (PENDIENTE / APROBADO / null). */
  conceptos_aprobacion_estado?: string | null;
}

export interface FotoTrasladoNueva {
  photoUrl: string;
  photoId?: string | null;
  fecha_hora?: string;
  tipo?: TipoFotoProceso;
}

export const FOTOS_TRASLADO_LIVE_KEY = "orden-trabajo-fotos-traslado";
/** Cada cuánto se consulta el estado de las fotos (reflejo en tiempo real). */
export const FOTOS_TRASLADO_POLL_MS = 5000;

const { post } = erpApi();

const urlFotos = (id: string, flujo: FlujoFotos = "traslado") =>
  `${urlBackend}api/v1/orden-trabajo/${id}/fotos-${flujo}/`;

/** Flujo del snapshot tal como lo devuelve el backend ("TRASLADO" → "traslado"). */
const flujoDe = (snapshot?: FotosTrasladoSnapshot | null): FlujoFotos =>
  `${snapshot?.flujo || ""}`.toUpperCase() === "VISITA" ? "visita" : "traslado";

/**
 * Consulta de sondeo. Va con axios directo (no con `erpApi`) para que un fallo
 * puntual de red no dispare un toast de error cada pocos segundos: el siguiente
 * intento lo corrige solo.
 */
export async function getFotosTrasladoSnapshot(
  id: string,
  flujo: FlujoFotos = "traslado",
): Promise<FotosTrasladoSnapshot> {
  const token = useAuthStore.getState().token;
  const res = await axios.get(urlFotos(id, flujo), {
    headers: { Authorization: `Token ${token}` },
    timeout: 15000,
  });
  return res.data?.data as FotosTrasladoSnapshot;
}

export async function eliminarFotoTraslado(
  id: string,
  body: { etapa: EtapaFotoTraslado; photoUrl?: string | null; photoId?: string | null },
  flujo: FlujoFotos = "traslado",
): Promise<FotosTrasladoSnapshot> {
  const res = await post<FotosTrasladoSnapshot>(`${urlFotos(id, flujo)}eliminar/`, body, true);
  return res.data;
}

export async function agregarFotosTraslado(
  id: string,
  body: { etapa: EtapaFotoTraslado; fotos: FotoTrasladoNueva[] },
  flujo: FlujoFotos = "traslado",
): Promise<FotosTrasladoSnapshot> {
  const res = await post<FotosTrasladoSnapshot>(`${urlFotos(id, flujo)}agregar/`, body, true);
  return res.data;
}

/**
 * Estado EN VIVO de las fotos (traslado o visita). Es un query aparte del de la
 * OT: se refresca solo cada pocos segundos sin tocar el objeto que alimenta los
 * formularios (no resetea lo que el usuario esté editando).
 */
export function useFotosTrasladoLive(
  idOrdenTrabajo?: string | null,
  enabled = true,
  flujo: FlujoFotos = "traslado",
) {
  return useQuery({
    queryKey: [FOTOS_TRASLADO_LIVE_KEY, idOrdenTrabajo || "", flujo],
    queryFn: () => getFotosTrasladoSnapshot(idOrdenTrabajo!, flujo),
    enabled: enabled && !!idOrdenTrabajo,
    refetchInterval: FOTOS_TRASLADO_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

/** Escribe en caché el estado que devuelve eliminar/agregar (reflejo inmediato). */
export function useSetFotosTrasladoLive() {
  const queryClient = useQueryClient();
  return (snapshot?: FotosTrasladoSnapshot | null) => {
    if (!snapshot?.id_orden_trabajo) return;
    queryClient.setQueryData(
      [FOTOS_TRASLADO_LIVE_KEY, snapshot.id_orden_trabajo, flujoDe(snapshot)],
      snapshot,
    );
  };
}

/** Fuerza una consulta inmediata del estado en vivo (p. ej. tras un error). */
export function useRefrescarFotosTrasladoLive() {
  const queryClient = useQueryClient();
  // Sin flujo invalida ambos (la clave es un prefijo).
  return (idOrdenTrabajo?: string | null, flujo?: FlujoFotos) =>
    queryClient.invalidateQueries({
      queryKey: flujo
        ? [FOTOS_TRASLADO_LIVE_KEY, idOrdenTrabajo || "", flujo]
        : [FOTOS_TRASLADO_LIVE_KEY, idOrdenTrabajo || ""],
    });
}

/**
 * Superpone el estado en vivo sobre la OT (solo los campos de fotos y los que
 * indican si el traslado inició/finalizó). Si aún no hay estado en vivo, o es
 * de otra OT, devuelve la OT tal cual.
 */
export function aplicarFotosTrasladoLive<T>(ordenTrabajo: T, snapshot?: FotosTrasladoSnapshot | null): T {
  const ot: any = ordenTrabajo;
  if (!ot || !snapshot) return ordenTrabajo;
  if (`${ot.id_orden_trabajo || ""}` !== `${snapshot.id_orden_trabajo || ""}`) return ordenTrabajo;
  return {
    ...ot,
    urls_fotos_ingreso: snapshot.urls_fotos_ingreso,
    urls_fotos_proceso: snapshot.urls_fotos_proceso,
    urls_fotos_salida: snapshot.urls_fotos_salida,
    fecha_hora_inicio_instalacion: snapshot.fecha_hora_inicio_instalacion,
    fecha_hora_fin_instalacion: snapshot.fecha_hora_fin_instalacion,
    // Visitas: su inicio/fin van en campos propios.
    fecha_hora_inicio_visita: snapshot.fecha_hora_inicio_visita ?? ot.fecha_hora_inicio_visita,
    fecha_hora_fin_visita: snapshot.fecha_hora_fin_visita ?? ot.fecha_hora_fin_visita,
    estado_orden_trabajo: snapshot.estado_orden_trabajo ?? ot.estado_orden_trabajo,
  } as T;
}

/** OT con las fotos en vivo ya superpuestas + el estado crudo del sondeo. */
export function useOrdenTrabajoFotosLive<T>(
  ordenTrabajo: T,
  enabled = true,
  flujo: FlujoFotos = "traslado",
) {
  const id = (ordenTrabajo as any)?.id_orden_trabajo as string | undefined;
  const { data: snapshot, isFetched } = useFotosTrasladoLive(id, enabled, flujo);
  const ot = useMemo(() => aplicarFotosTrasladoLive(ordenTrabajo, snapshot), [ordenTrabajo, snapshot]);
  return { ot, snapshot: snapshot ?? null, enVivo: enabled && isFetched && !!snapshot };
}

const plural = (n: number) => (n === 1 ? "foto" : "fotos");

/**
 * Avisos al detectar cambios hechos por la OTRA parte entre dos sondeos:
 *  - "tecnico": Activaciones eliminó fotos (baja el conteo) y hay que volver a subirlas.
 *  - "activaciones": el técnico subió fotos (sube el conteo).
 * El primer estado recibido solo fija la referencia (no avisa).
 */
export function useAvisosFotosTraslado(
  snapshot: FotosTrasladoSnapshot | null | undefined,
  modo: "tecnico" | "activaciones" | null,
) {
  const previo = useRef<{ id: string; ingreso: number; proceso: number; finalizacion: number } | null>(null);
  const ingreso = snapshot?.conteo?.ingreso;
  const proceso = snapshot?.conteo?.proceso;
  const finalizacion = snapshot?.conteo?.finalizacion;
  const id = snapshot?.id_orden_trabajo;

  useEffect(() => {
    if (!id || ingreso == null || proceso == null || finalizacion == null) return;
    const actual = { id, ingreso, proceso, finalizacion };
    const antes = previo.current;
    previo.current = actual;
    if (!modo || !antes || antes.id !== id) return;

    const etapas: { clave: "ingreso" | "proceso" | "finalizacion"; nombre: string }[] = [
      { clave: "ingreso", nombre: "ingreso" },
      { clave: "proceso", nombre: "proceso" },
      { clave: "finalizacion", nombre: "finalización" },
    ];
    for (const { clave, nombre } of etapas) {
      const diff = actual[clave] - antes[clave];
      if (modo === "tecnico" && diff < 0) {
        const n = Math.abs(diff);
        toast.warning(
          `Activaciones eliminó ${n} ${plural(n)} de ${nombre}. Vuelve a subirla${n === 1 ? "" : "s"} en la pestaña FOTOS.`,
          { autoClose: 10000 },
        );
      }
      if (modo === "activaciones" && diff > 0) {
        toast.info(`El técnico subió ${diff} ${plural(diff)} de ${nombre}.`);
      }
    }
  }, [id, ingreso, proceso, finalizacion, modo]);
}
